import { createTtlDedupe, TtlDedupe } from './Common'
import {
  ClientErrorApp,
  ClientErrorSeverity,
  ReportClientErrorRequest,
  ReportClientErrorResponse
} from '../types/ClientError'

// Shared between the frontends (pre-send dedupe) and the backend handler (post-sanitize dedupe),
// so both sides agree on what "the same error" means. No SDK imports here — the transport is
// injected by each app, which keeps firebase/functions out of the backend bundle and
// firebase-admin out of the browser bundles.

export const CLIENT_ERROR_CLIENT_DEDUPE_MS = 60_000
export const CLIENT_ERROR_SERVER_DEDUPE_MS = 5 * 60_000

export type ClientErrorReportResult = ReportClientErrorResponse | { skipped: true } | { error: unknown }

export type ClientErrorUser = { email?: string; id?: string }

export const buildClientErrorFingerprint = (parts: {
  app: string
  severity: ClientErrorSeverity
  name: string
  message: string
  context?: string
}): string => `${parts.app}|${parts.severity}|${parts.name}|${parts.message}|${parts.context || ''}`

/**
 * Builds the sendErrorToSlack / sendCriticalErrorToSlack pair for one frontend.
 * @param {object} params - Wiring for this app.
 * @param {ClientErrorApp} params.app - Which frontend is reporting.
 * @param {Function} params.isEnabled - Checked per call so config/env changes are respected.
 * @param {Function} params.send - Transport (an httpsCallable wrapper owned by the app).
 * @param {Function} [params.onSkipped] - Called instead of sending when reporting is disabled.
 * @param {Function} [params.getUrl] - Current page URL; defaults to window.location.href.
 * @return {object} `reportError` and `reportCriticalError`.
 */
export const createClientErrorReporter = (params: {
  app: ClientErrorApp
  isEnabled: () => boolean
  send: (payload: ReportClientErrorRequest) => Promise<ReportClientErrorResponse>
  onSkipped?: (error: Error, user?: ClientErrorUser, context?: string) => void
  getUrl?: () => string | undefined
}) => {
  // Pre-send guard so a render loop or a retrying query does not fire one call per occurrence.
  // The backend dedupes too; this just avoids the round trips. Built on first use, not at module
  // eval — apps construct their reporter at import time, and a shared lib must not run work then.
  let dedupe: TtlDedupe | undefined
  const getDedupe = () => (dedupe ||= createTtlDedupe(CLIENT_ERROR_CLIENT_DEDUPE_MS, 100))

  const report = (
    error: Error,
    user?: ClientErrorUser,
    context?: string,
    severity: ClientErrorSeverity = 'error'
  ): Promise<ClientErrorReportResult> => {
    if (!params.isEnabled()) {
      params.onSkipped?.(error, user, context)
      return Promise.resolve({ skipped: true })
    }

    const fingerprint = buildClientErrorFingerprint({
      app: params.app,
      severity,
      name: error.name,
      message: error.message,
      context
    })
    if (getDedupe().isDuplicate(fingerprint)) return Promise.resolve({ delivered: false, reason: 'duplicate' })

    const defaultUrl = () => (typeof window === 'undefined' ? undefined : window.location.href)

    return (
      params
        .send({
          app: params.app,
          severity,
          name: error.name,
          message: error.message,
          stack: error.stack,
          context,
          url: (params.getUrl || defaultUrl)(),
          userEmail: user?.email,
          userId: user?.id
        })
        // Never let error reporting throw — that would report an error about reporting an error.
        .catch((err) => ({ error: err }))
    )
  }

  return {
    reportError: (error: Error, user?: ClientErrorUser, context?: string) => report(error, user, context, 'error'),
    reportCriticalError: (error: Error, user?: ClientErrorUser, context?: string) =>
      report(error, user, context, 'critical')
  }
}
